import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const PAGE_SIZE = 20;

const truncate = (text, max = 20) => {
  const value = (text || '').trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max)}...`;
};

const escapeIlike = (value) =>
  value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_').replace(/,/g, ' ');

const InquiriesList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);

  const [searchTarget, setSearchTarget] = useState('content');
  const [query, setQuery] = useState('');
  const [statusProcessing, setStatusProcessing] = useState(true);
  const [statusCompleted, setStatusCompleted] = useState(true);
  const currentFiltersRef = useRef(null);
  const observerTarget = useRef(null);

  const resolveStatusFilter = (processing, completed) => {
    if (processing && !completed) return ['processing'];
    if (!processing && completed) return ['completed'];
    return null; // 둘 다 또는 둘 다 아님 → 전체
  };

  const buildQuery = async (filters) => {
    let request = supabase
      .from('inquiries')
      .select('id, title, content, status, created_at, user_id, users(name, email)', { count: 'exact' })
      .order('created_at', { ascending: false });

    const statuses = resolveStatusFilter(filters.statusProcessing, filters.statusCompleted);
    if (statuses) {
      request = request.in('status', statuses);
    }

    const keyword = filters.query?.trim();
    if (keyword) {
      const safeKeyword = escapeIlike(keyword);
      if (filters.searchTarget === 'author') {
        const { data: matchedUsers, error: userError } = await supabase
          .from('users')
          .select('id')
          .or(`email.ilike.%${safeKeyword}%,name.ilike.%${safeKeyword}%`);

        if (userError) throw userError;

        const userIds = (matchedUsers || []).map((u) => u.id);
        if (userIds.length === 0) {
          return { empty: true };
        }
        request = request.in('user_id', userIds);
      } else {
        request = request.ilike('content', `%${safeKeyword}%`);
      }
    }

    return { request };
  };

  const fetchInquiries = async (filters) => {
    setLoading(true);
    setError(null);
    setOffset(0);
    setHasMore(true);
    setInquiries([]);
    currentFiltersRef.current = filters;

    try {
      const built = await buildQuery(filters);
      if (built.empty) {
        setInquiries([]);
        setHasMore(false);
        return;
      }

      const { data, error: fetchError } = await built.request.range(0, PAGE_SIZE - 1);

      if (fetchError) {
        setError(fetchError.message || '문의 목록을 불러오지 못했습니다.');
      } else {
        setInquiries(data || []);
        setOffset(PAGE_SIZE);
        setHasMore((data?.length || 0) >= PAGE_SIZE);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || '목록 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    const filters = currentFiltersRef.current;
    if (loadingMore || !hasMore || !filters) return;

    setLoadingMore(true);
    try {
      const built = await buildQuery(filters);
      if (built.empty) {
        setHasMore(false);
        return;
      }

      const { data, error: fetchError } = await built.request.range(offset, offset + PAGE_SIZE - 1);

      if (fetchError) {
        setError(fetchError.message || '추가 목록을 불러오지 못했습니다.');
      } else {
        const newData = data || [];
        setInquiries((prev) => [...prev, ...newData]);
        setOffset((prev) => prev + PAGE_SIZE);
        setHasMore(newData.length >= PAGE_SIZE);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    fetchInquiries({
      searchTarget,
      query,
      statusProcessing,
      statusCompleted,
    });
  };

  const handleStatusToggle = (type, checked) => {
    const nextProcessing = type === 'processing' ? checked : statusProcessing;
    const nextCompleted = type === 'completed' ? checked : statusCompleted;
    if (type === 'processing') setStatusProcessing(checked);
    if (type === 'completed') setStatusCompleted(checked);

    fetchInquiries({
      searchTarget,
      query,
      statusProcessing: nextProcessing,
      statusCompleted: nextCompleted,
    });
  };

  const handleReset = () => {
    setSearchTarget('content');
    setQuery('');
    setStatusProcessing(true);
    setStatusCompleted(true);
    fetchInquiries({
      searchTarget: 'content',
      query: '',
      statusProcessing: true,
      statusCompleted: true,
    });
  };

  const handleRowClick = (id) => {
    navigate(`/inquiries/${id}`);
  };

  const statusText = (status) => (status === 'completed' ? '답변 완료' : '처리중');
  const statusClass = (status) => (status === 'completed' ? 'badge badge-success' : 'badge badge-warning');
  const authorId = (item) => item.users?.email || item.user_id?.slice?.(0, 8) || '알 수 없음';

  useEffect(() => {
    const statusParam = searchParams.get('status');
    let initProcessing = true;
    let initCompleted = true;
    if (statusParam === 'processing') {
      initProcessing = true;
      initCompleted = false;
    } else if (statusParam === 'completed') {
      initProcessing = false;
      initCompleted = true;
    }

    setStatusProcessing(initProcessing);
    setStatusCompleted(initCompleted);
    fetchInquiries({
      searchTarget: 'content',
      query: '',
      statusProcessing: initProcessing,
      statusCompleted: initCompleted,
    });
  }, [searchParams]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const target = observerTarget.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [hasMore, loadingMore, loading, offset]);

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">1대1 문의 게시판</h6>
      </div>

      <div className="card-body">
        <form className="mb-4 p-3 bg-light rounded border" onSubmit={handleSearch}>
          <div className="form-row align-items-end">
            <div className="form-group col-md-2 mb-2 mb-md-0">
              <label className="small font-weight-bold mb-1">검색대상</label>
              <select
                className="form-control form-control-sm"
                value={searchTarget}
                onChange={(e) => setSearchTarget(e.target.value)}
              >
                <option value="content">작성내용</option>
                <option value="author">작성자</option>
              </select>
            </div>
            <div className="form-group col-md-4 mb-2 mb-md-0">
              <label className="small font-weight-bold mb-1">검색어</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder={searchTarget === 'author' ? '작성자(아이디/이름) 검색' : '작성내용 검색'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="form-group col-md-3 mb-2 mb-md-0">
              <label className="small font-weight-bold mb-1 d-block">처리상태</label>
              <div className="form-check form-check-inline mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="statusProcessing"
                  checked={statusProcessing}
                  onChange={(e) => handleStatusToggle('processing', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="statusProcessing">
                  처리중
                </label>
              </div>
              <div className="form-check form-check-inline mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="statusCompleted"
                  checked={statusCompleted}
                  onChange={(e) => handleStatusToggle('completed', e.target.checked)}
                />
                <label className="form-check-label" htmlFor="statusCompleted">
                  답변 완료
                </label>
              </div>
            </div>
            <div className="form-group col-md-3 mb-0 text-md-right">
              <button type="submit" className="btn btn-primary btn-sm mr-2" disabled={loading}>
                조회
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={handleReset} disabled={loading}>
                초기화
              </button>
            </div>
          </div>
        </form>

        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div>로딩 중...</div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="thead-light">
                  <tr>
                    <th style={{ width: '40%' }}>작성내용</th>
                    <th style={{ width: '20%' }}>작성자(아이디)</th>
                    <th style={{ width: '25%' }}>작성일시</th>
                    <th style={{ width: '15%' }}>처리상태</th>
                  </tr>
                </thead>
                <tbody>
                  {inquiries.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">
                        등록된 문의가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    inquiries.map((item) => (
                      <tr key={item.id} style={{ cursor: 'pointer' }} onClick={() => handleRowClick(item.id)}>
                        <td title={item.content || item.title}>{truncate(item.content || item.title, 20)}</td>
                        <td title={authorId(item)}>{authorId(item)}</td>
                        <td>{item.created_at ? new Date(item.created_at).toLocaleString() : '-'}</td>
                        <td>
                          <span className={statusClass(item.status)}>{statusText(item.status)}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div ref={observerTarget} className="text-center py-4">
              {loadingMore && (
                <div className="text-muted">
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  더 불러오는 중...
                </div>
              )}
              {!hasMore && inquiries.length > 0 && (
                <small className="text-muted">모든 데이터를 불러왔습니다.</small>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InquiriesList;
